import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextItem, useZakeke } from 'zakeke-configurator-react';
import { additionalPropsContext, useCustomizationParams, useViewerState } from '../../context/ViewerContext';

import {
  ViewerAttributeInterface,
  ViewerGroupInterface,
  mapViewerOptions,
  ViewerOptions,
  ViewerCustomizationInterface,
  ViewerItemInterface,
  instanceOfViewerGroupCustomizations,
  CUSTOMIZATION_GROUP_CODES,
  ViewerStepInterface,
  ViewerSelectionItemArgs,
  getSelectedItem,
  CustomizationAssets,
  ViewerGroupInputItemsInterface,
  ViewerOptionInterfaceInput,
  ViewerAttributeInputInterface,
} from '../../core';
import { FilterGroupsByOption, mapSpecialGroup } from '../../core/services/filter-options';
import { EditTextItem } from '../../components/MenuChoiceCustomizations/ItemText';
import { openAttributesOnLoad } from '../../core/services/open-attributes-onload/open-attributes-onload';
const useViewerOptions = () => {
  const {
    isSceneLoading,
    currentTemplate,
    product,
    groups,
    items,
    templates,
    setTemplate,
    setCamera,
    selectOption,
    setItemText,
    setItemImage,
    setItemFontFamily,
    setItemBold,
    setItemItalic,
    addFocusAttributesListener,
    clearListeners,
    isAreaVisible,
    addItemText,
    addItemImage,
    removeItem,
  } = useZakeke();

  const { setViewerState, viewerState } = useViewerState();
  const { customizationParams } = useCustomizationParams();
  const [t] = useTranslation();
  const [viewerOptions, setViewerOptions] = useState<ViewerOptions>({
    groups: [],
    cameras: [],
    specialGroup: null,
  });

  const [bulkData, setBulkData] = useState<ViewerGroupInputItemsInterface | null>(null);
  const [customizationAssets, setCustomizationAssets] = useState<CustomizationAssets | null>(null);

  const [isItemInit, setItemInit] = useState(false);
  const { additionalProps, setAdditionalProps } = additionalPropsContext();

  const onUserClickTemplate = useCallback(
    (arg: ViewerSelectionItemArgs) => {
      const selectedOption = getSelectedItem(arg, viewerOptions);
      if (!!selectedOption) {
        let openedAttributes = viewerState.openedAttributes;
        const attrId = 'attribute' in selectedOption ? selectedOption.attribute?.id : null;
        openedAttributes = [attrId as string];

        setViewerState({
          ...viewerState,
          groupId: selectedOption.group.id || null,
          stepId: ('step' in selectedOption ? selectedOption.step?.id : null) || null,
          attributeId: attrId || null,
          openedAttributes,
        });
        if (selectedOption.group.cameraLocationId) {
          setCamera(selectedOption.group.cameraLocationId);
        }
      }
    },
    [setCamera, setViewerState, viewerOptions, viewerState]
  );

  const onSelectedGroup = (group: ViewerGroupInterface): void => {
    if (group.id !== viewerState?.groupId && group.cameraLocationId) {
      setCamera(group.cameraLocationId);
    }
    setViewerState({
      ...viewerState,
      groupId: group.id,
      stepId: null,
      attributeId: null,
    });
  };

  const onSelectedStep = (step: ViewerStepInterface | null): void => {
    setViewerState({
      ...viewerState,
      stepId: !step || viewerState.stepId === step?.id ? null : step.id,
      attributeId: null,
    });
  };

  const onSelectedAttribute = (attribute: ViewerAttributeInterface | null): void => {
    if (attribute && attribute.cameraLocationId) {
      setCamera(attribute.cameraLocationId);
    }

    let changedOpenedAttributes = viewerState.openedAttributes;

    if (viewerState.openedAttributes.includes(attribute?.id as string)) {
      changedOpenedAttributes = viewerState.openedAttributes.filter((id) => id !== attribute?.id);
    } else {
      changedOpenedAttributes = [...viewerState.openedAttributes, attribute?.id as string];
    }
    setViewerState({
      ...viewerState,
      openedAttributes: changedOpenedAttributes,
      attributeId: !attribute || viewerState.attributeId === attribute?.id ? null : attribute.id,
    });
  };

  const onSelectedOption = (opt: ViewerItemInterface): void => {
    const key = viewerState.attributeId as string;
    let currentAttributes = additionalProps.attributes;

    if (key && currentAttributes[key]) {
      currentAttributes[key].option = {
        value: t(opt.id),
        code: opt.id,
      };
      setAdditionalProps({
        ...additionalProps,
        attributes: currentAttributes,
      });
    }

    selectOption(opt.externalCode);
  };

  const onSaveText = (customization: ViewerCustomizationInterface, text: string | null) => {
    setItemText(customization.guid, text || '');
  };

  const onSaveFont = (customization: ViewerCustomizationInterface, font: string | null) => {
    setItemFontFamily(customization.guid, font || '');
  };

  const onSaveBold = (customization: ViewerCustomizationInterface, bold: boolean | null) => {
    setItemBold(customization.guid, bold || false);
  };

  const onSaveItalic = (customization: ViewerCustomizationInterface, italic: boolean | null) => {
    setItemItalic(customization.guid, italic || false);
  };

  const onAddItemText = async (item: EditTextItem, actualAreaId: number) => {
    await addItemText(item, actualAreaId);
  };

  const onAddItemImage = async (imageId: number, actualAreaId: number) => {
    await addItemImage(imageId, actualAreaId);
  };

  const onSetItemImage = (guidId: string, imageId: number) => {
    setItemImage(guidId, imageId);
  };

  const onRemoveItem = async (guid: string) => {
    await removeItem(guid);
  };

  const setBulkDataOptionValue = (option: ViewerOptionInterfaceInput, value: string) => {
    if (!bulkData) return;

    // find the option in the bulkData
    const attribute = getAttributeByOption(option);
    if (!attribute) return;
    const options = attribute.options as ViewerOptionInterfaceInput[];

    options.forEach((opt) => {
      if (opt.id === option.id) {
        opt.value = Number(value);
      }
    });
    attribute.options = options;

    setBulkData({
      ...bulkData,
      attributes: bulkData?.attributes.map((attr) => {
        if (attr.id === attribute.id) {
          return attribute;
        }
        return attr;
      }),
    });
  };

  const getAttributeByOption = (option: ViewerOptionInterfaceInput): ViewerAttributeInputInterface | undefined => {
    return bulkData?.attributes.find((attr) => {
      const options = attr.options as ViewerOptionInterfaceInput[];
      return options.find((opt) => opt.id === option.id);
    });
  };

  const setBulkDataAttributePrice = (attribute: ViewerAttributeInputInterface, price: number) => {
    if (!bulkData || !price) return;

    attribute.price = Number(price);
    setBulkData({
      ...bulkData,
      attributes: bulkData?.attributes.map((attr) => {
        if (attr.id === attribute.id) {
          return attribute;
        }
        return attr;
      }),
    });
  };

  const resetBulkDataValues = () => {
    if (!bulkData) return;

    const attributes = bulkData.attributes;
    attributes.forEach((attr, index) => {
      attr.options.forEach((opt) => {
        opt.value = opt.selected && index === 0 ? 1 : 0;
      });
      attr.price = 0;
    });

    setBulkData({
      ...bulkData,
      attributes,
    });
  };

  useEffect(() => {
    const newViewerOptions = mapViewerOptions(
      {
        groups,
        customization: {
          name: `${CUSTOMIZATION_GROUP_CODES.GROUP_CODE}#CUSTOM`,
          items: items as TextItem[],
          currentTemplate,
          productAreas: product?.areas.filter((area) => isAreaVisible(area.id)) ?? [],
        },
      },
      customizationAssets
    );

    const openedAttributes = openAttributesOnLoad(customizationParams, newViewerOptions.groups);

    if (openedAttributes) {
      setViewerState({
        ...viewerState,
        openedAttributes,
      });
    }

    const mappedGroups = mapSpecialGroup(newViewerOptions, customizationParams, bulkData, setBulkData);

    // Filter group based on an option choice
    const filtered_groups_by_option = FilterGroupsByOption(customizationParams, mappedGroups.groups, onSelectedOption);

    const urlParamsGet = new URLSearchParams(window.location.search);
    const customizationComposition = urlParamsGet.get('composition');
    const filterGroupsComposition = customizationParams?.filterGroupsComposition;
    const groupsFiltered =
      !!filterGroupsComposition && customizationComposition
        ? filtered_groups_by_option
            .filter((g) => !customizationParams?.filterGroupsComposition?.includes(g.id))
            .sort((a, b) => {
              if (a.id === 'Other') return 1;
              if (b.id === 'Other') return -1;
              return a.displayOrder - b.displayOrder;
            })
        : filtered_groups_by_option;

    console.log(groupsFiltered);

    if (groupsFiltered) {
      setViewerOptions({
        ...newViewerOptions,
        groups: groupsFiltered,
      });
    } else {
      setViewerOptions(mappedGroups);
    }
  }, [groups, items, customizationAssets]);

  useEffect(() => {
    if (!!viewerState && !viewerState.groupId && viewerOptions.groups.length > 0 && !instanceOfViewerGroupCustomizations(viewerOptions.groups[0])) {
      const group = viewerOptions.groups[0];

      setViewerState({
        ...viewerState,
        groupId: group.id,
      });
      if (group && group.cameraLocationId) {
        setCamera(group.cameraLocationId);
      }
    }
    // eslint-disable-next-line
  }, [viewerState, viewerOptions]);

  useEffect(() => {
    addFocusAttributesListener(onUserClickTemplate);

    return clearListeners;
  }, [addFocusAttributesListener, clearListeners, onUserClickTemplate]);

  useEffect(() => {
    if (!!templates?.length) {
      setTemplate(templates[0].id);
    }
    // eslint-disable-next-line
  }, [templates]);

  useEffect(() => {
    if (!isItemInit && !!items?.length && items[0] instanceof TextItem) {
      setItemInit(true);
      console.log('items', items[0]);
      if (items[0].text === '') setItemText(items[0].guid, '​');
    }
    // eslint-disable-next-line
  }, [items, isItemInit]);

  return {
    isLoading: isSceneLoading || !product,
    isSceneReady: !isSceneLoading,
    viewerOptions,
    viewerState,
    bulkData,
    setBulkData,
    resetBulkDataValues,
    setViewerState,
    setCustomizationAssets,
    onSelectedGroup,
    onSelectedStep,
    onSelectedAttribute,
    onSelectedOption,
    onSetItemImage,
    onSaveText,
    onSaveFont,
    onSaveBold,
    onSaveItalic,
    onAddItemText,
    onAddItemImage,
    onRemoveItem,
    setBulkDataOptionValue,
    setBulkDataAttributePrice,
  };
};

export { useViewerOptions };
